import { useState } from "react";
import { motion } from "framer-motion";
import { Copy, Check, Sparkles, FileWarning, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { SEVERITY, CATEGORY_LABEL } from "@/lib/severity";

function CopyPrompt({ text, id }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text; document.body.appendChild(ta); ta.select();
      document.execCommand("copy"); document.body.removeChild(ta);
    }
    setCopied(true);
    toast.success("Fix prompt copied to clipboard");
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <div className="border border-border bg-[#080808]">
      <div className="flex items-center justify-between px-3 py-2 border-b border-border">
        <span className="font-mono text-[10px] uppercase tracking-widest text-neon flex items-center gap-1.5">
          <Sparkles className="w-3 h-3" /> AI Fix Prompt
        </span>
        <button
          data-testid={`copy-prompt-${id}`}
          onClick={copy}
          className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wide text-black bg-neon px-2.5 py-1 hard-shadow-hover"
        >
          {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <p className="p-3 font-mono text-xs text-[#A3A3A3] leading-relaxed whitespace-pre-wrap">{text}</p>
    </div>
  );
}

export function IssuesList({ issues }) {
  if (!issues.length) {
    return (
      <div className="border border-neon/40 bg-neon/5 p-10 text-center" data-testid="no-issues">
        <ShieldCheck className="w-10 h-10 text-neon mx-auto mb-3 glow-neon" />
        <p className="font-display font-bold text-xl text-neon">No issues detected</p>
        <p className="text-muted-foreground text-sm mt-2">The scanner found no common security mistakes. Still, review manually before shipping.</p>
      </div>
    );
  }

  return (
    <div data-testid="issues-list">
      <div className="flex items-center gap-2 mb-4">
        <FileWarning className="w-4 h-4 text-muted-foreground" />
        <h2 className="font-display font-bold text-lg">Findings <span className="text-muted-foreground font-mono text-sm">({issues.length})</span></h2>
      </div>
      <Accordion type="multiple" className="space-y-2">
        {issues.map((issue, idx) => {
          const sev = SEVERITY[issue.severity] || SEVERITY.low;
          return (
            <motion.div
              key={issue.id}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: Math.min(idx * 0.03, 0.4) }}
            >
              <AccordionItem
                value={issue.id}
                className={`border border-border border-l-2 ${sev.border} bg-surface`}
                data-testid={`issue-item-${idx}`}
              >
                <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-surface2 transition-colors [&>svg]:text-muted-foreground">
                  <div className="flex items-start gap-3 text-left w-full pr-2">
                    <span className={`font-mono text-[10px] font-bold px-1.5 py-0.5 border shrink-0 mt-0.5 ${sev.text}`} style={{ borderColor: sev.color }}>
                      {sev.label}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm text-foreground truncate">{issue.title}</p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <span className="font-mono text-[11px] text-muted-foreground truncate">{issue.file}{issue.line ? `:${issue.line}` : ""}</span>
                        <span className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground/70 border border-border px-1">{CATEGORY_LABEL[issue.category] || issue.category}</span>
                        {issue.source === "ai" && <span className="font-mono text-[10px] uppercase text-neon flex items-center gap-1"><Sparkles className="w-2.5 h-2.5" />AI</span>}
                      </div>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4 space-y-3">
                  {issue.snippet && (
                    <pre className="bg-[#080808] border border-border p-3 overflow-x-auto font-mono text-xs text-warning"><code>{issue.snippet}</code></pre>
                  )}
                  <div className="bg-surface2 border border-border p-3">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">What's wrong</p>
                    <p className="text-sm text-foreground/90 leading-relaxed">{issue.explanation}</p>
                  </div>
                  <CopyPrompt text={issue.fix_prompt} id={idx} />
                </AccordionContent>
              </AccordionItem>
            </motion.div>
          );
        })}
      </Accordion>
    </div>
  );
}
